// apps/client/src/utils/compliance.ts
//
// Thin client-side wrapper around the schema-driven scorer in
// @avanzu/rdti-schema. The legacy hardcoded FIELD_RULES table is gone — rules
// now live alongside each FieldDef in packages/rdti-schema/src/versions/*.
//
// Backwards-compatible exports are preserved so the existing
// ComplianceScoreWidget + ProjectEditor keep working until they're switched
// to the dynamic renderer.

import { Project, ComplianceFieldScore, ProjectComplianceScore, RDFieldName } from '@avanzu/types';
import {
  findField,
  getFieldsForSection,
  resolveSchemaVersionForTaxYear,
  scoreField,
  scoreSection,
  type FieldComplianceScore,
  type RDTIResponses,
} from '@avanzu/rdti-schema';

/**
 * Legacy RDFieldName → FieldDef.key mapping. The hardcoded FY24 field set
 * happens to namespace to `project.*` and a sibling set on `core.*`/
 * `support.*` — when an old caller passes "hypothesis" we resolve it
 * against the project section first, then activity sections.
 */
const LEGACY_PROJECT_KEY_BY_NAME: Record<string, string> = {
  description: 'project.description',
  objectives: 'project.objectives',
  hypothesis: 'project.hypothesis',
  technicalUncertainty: 'project.technicalUncertainty',
  priorResearch: 'project.priorResearch',
  newKnowledge: 'project.newKnowledge',
  systematicApproach: 'project.systematicApproach',
  evidence: 'project.evidence',
};

const LEGACY_ACTIVITY_KEY_BY_NAME: Record<string, { section: 'coreActivity' | 'supportingActivity'; key: string }> = {
  scientificPrinciples:     { section: 'coreActivity',       key: 'core.scientificPrinciples' },
  experimentalMethodology:  { section: 'coreActivity',       key: 'core.experimentalMethodology' },
  expectedOutcomes:         { section: 'coreActivity',       key: 'core.expectedOutcomes' },
  priorKnowledgeGap:        { section: 'coreActivity',       key: 'core.priorKnowledgeGap' },
  supportDescription:       { section: 'supportingActivity', key: 'support.supportDescription' },
  dominantPurpose:          { section: 'supportingActivity', key: 'support.dominantPurpose' },
};

function toLegacyShape(s: FieldComplianceScore): ComplianceFieldScore {
  return {
    score: s.score,
    issues: s.issues,
    suggestions: s.suggestions,
    hasRedFlags: s.hasRedFlags,
    redFlags: s.redFlags,
  };
}

/**
 * Look up a project response value, falling back to the legacy deprecated
 * column for callers that pass an old-style Project still populated by the
 * server pre-migration.
 */
function readLegacyValue(project: Project, fieldName: RDFieldName): string | null | undefined {
  if (project.responses && project.responses[`project.${fieldName}`] !== undefined) {
    return project.responses[`project.${fieldName}`] as string | null;
  }
  return (project as unknown as Record<string, string | null | undefined>)[fieldName];
}

/**
 * Check a single legacy field. Resolves against FY24 schema (where the legacy
 * field names live) and runs the schema-driven scorer.
 */
export function checkFieldCompliance(
  fieldName: RDFieldName,
  value: string | null | undefined,
): ComplianceFieldScore {
  const projectKey = LEGACY_PROJECT_KEY_BY_NAME[fieldName];
  const activityRef = LEGACY_ACTIVITY_KEY_BY_NAME[fieldName];

  let field;
  if (projectKey) {
    field = findField('FY24', 'project', projectKey);
  } else if (activityRef) {
    field = findField('FY24', activityRef.section, activityRef.key);
  }

  if (!field) {
    return {
      score: 0,
      issues: [`Unknown field: ${fieldName}`],
      suggestions: [],
      hasRedFlags: false,
      redFlags: [],
    };
  }

  return toLegacyShape(scoreField(field, value));
}

/**
 * Project-level compliance. Uses `project.schemaVersion` + `project.responses`
 * when present, otherwise falls back to the legacy deprecated columns for
 * pre-migration data.
 */
export function calculateProjectCompliance(project: Project): ProjectComplianceScore {
  const version = project.schemaVersion ?? resolveSchemaVersionForTaxYear(project.taxYear);

  let responses: RDTIResponses;
  if (project.responses && Object.keys(project.responses).length > 0) {
    responses = project.responses;
  } else {
    // Synthesize responses from the legacy columns so older data still scores.
    responses = {};
    for (const fieldName of Object.keys(LEGACY_PROJECT_KEY_BY_NAME)) {
      const key = LEGACY_PROJECT_KEY_BY_NAME[fieldName];
      const value = readLegacyValue(project, fieldName as RDFieldName);
      if (value != null) responses[key] = value;
    }
  }

  const section = scoreSection(version, 'project', responses);

  // Map FieldDef.key results back into the legacy RDFieldName-keyed shape
  // that ComplianceScoreWidget still expects.
  const fieldScores: ProjectComplianceScore['fieldScores'] = {};
  const projectFields = getFieldsForSection(version, 'project');
  for (const field of projectFields) {
    const score = section.fieldScores[field.key];
    if (!score) continue;
    // legacy name = last segment of field.key (e.g. "project.hypothesis" → "hypothesis")
    const legacyName = field.key.split('.').slice(1).join('.') as RDFieldName;
    fieldScores[legacyName] = toLegacyShape(score);
  }

  return {
    overallScore: section.overallScore,
    fieldScores,
    completeness: section.completeness,
    criticalIssues: section.criticalIssues,
  };
}

/** Map a legacy RDFieldName to its human-readable label, sourced from the schema. */
export function formatFieldName(fieldName: RDFieldName): string {
  const projectKey = LEGACY_PROJECT_KEY_BY_NAME[fieldName];
  const activityRef = LEGACY_ACTIVITY_KEY_BY_NAME[fieldName];
  if (projectKey) {
    return findField('FY24', 'project', projectKey)?.label ?? fieldName;
  }
  if (activityRef) {
    return findField('FY24', activityRef.section, activityRef.key)?.label ?? fieldName;
  }
  return fieldName;
}

export function getComplianceColor(score: number): 'error' | 'warning' | 'success' {
  if (score < 50) return 'error';
  if (score < 75) return 'warning';
  return 'success';
}

export function getComplianceLabel(score: number): string {
  if (score < 50) return 'Needs Improvement';
  if (score < 75) return 'Good';
  if (score < 90) return 'Very Good';
  return 'Excellent';
}
